import { useEffect, useMemo, useState } from 'react';
import { Ban } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { checkTagName, parseTagNameLines, tagNameCheckMessage } from './lib/tagNormalize';

interface TagTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 既存のタグ種別名。重複判定はアカウント全体で行う */
  existingTypeNames: string[];
  /** 名前の変更なら変更前の名前。新規作成なら undefined */
  editingName?: string;
  submitting: boolean;
  onSubmit: (name: string, values: string[]) => void;
}

/**
 * タグ種別の作成・名前の変更ダイアログ。
 *
 * 作成時はタグ名もまとめて入力できる。種別を作ってすぐ使い始められるようにするため。
 */
export function TagTypeDialog({
  open,
  onOpenChange,
  existingTypeNames,
  editingName,
  submitting,
  onSubmit,
}: TagTypeDialogProps) {
  const isEditing = editingName !== undefined;
  const [name, setName] = useState('');
  const [valuesText, setValuesText] = useState('');

  const comparisonNames = useMemo(
    () => (isEditing ? existingTypeNames.filter((n) => n !== editingName) : existingTypeNames),
    [existingTypeNames, editingName, isEditing],
  );

  const check = useMemo(() => checkTagName(name, comparisonNames), [name, comparisonNames]);
  const values = useMemo(() => parseTagNameLines(valuesText), [valuesText]);
  const blocked = check.kind === 'duplicate' || check.kind === 'too_long';
  const submittable = check.kind !== 'empty' && !blocked;

  // 親が open を true にして開くため、開いたタイミングで入力欄を作り直す
  useEffect(() => {
    if (open) {
      setName(editingName ?? '');
      setValuesText('');
    }
  }, [open, editingName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'タグ種別の名前を変更' : 'タグ種別を追加'}</DialogTitle>
          <DialogDescription>
            1店舗に付けられるのは、1つの種別につき1つのタグ名だけです。複数の軸で分けたいときは種別を分けてください。
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tag-type-name">種別名</Label>
            <Input
              id="tag-type-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: 分析対象、都道府県、店舗ランク"
              autoFocus
            />
          </div>

          {!isEditing && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="tag-type-values">タグ名（1行に1つ・後からでも追加できます）</Label>
              <Textarea
                id="tag-type-values"
                rows={4}
                value={valuesText}
                onChange={(e) => setValuesText(e.target.value)}
                placeholder={'東京都\n大阪府\n神奈川県'}
              />
              <span className="text-xs text-gray-500">
                {values.length > 0
                  ? `${values.length}件のタグ名を同時に作成します`
                  : 'まとめて貼り付けられます。空のままでも作成できます。'}
              </span>
            </div>
          )}

          {blocked && (
            <p className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{tagNameCheckMessage(check, name)}</span>
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            type="button"
            disabled={!submittable || submitting}
            onClick={() => onSubmit(name, values)}
          >
            {isEditing ? '変更' : '作成'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
